import {
  NationalOverview, RegionSummary, SchoolSummary, ClassSummary,
  LevelMetrics, CompetencyBucket, AttendanceTrend, TopPerformer, AlertItem,
} from '../types';

// ========== Helper: build consistent competency buckets ==========
function mkBuckets(total: number, exPct: number, goodPct: number, avgPct: number): CompetencyBucket[] {
  const ex = Math.round(total * exPct / 100);
  const gd = Math.round(total * goodPct / 100);
  const av = Math.round(total * avgPct / 100);
  const ba = total - ex - gd - av;
  return [
    { level: 'Excellent', count: ex, percent: +(ex / total * 100).toFixed(1) },
    { level: 'Good', count: gd, percent: +(gd / total * 100).toFixed(1) },
    { level: 'Average', count: av, percent: +(av / total * 100).toFixed(1) },
    { level: 'Below Average', count: ba, percent: +(ba / total * 100).toFixed(1) },
  ];
}

const monthLabels = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'];

function mkTrend(base: number): AttendanceTrend[] {
  return monthLabels.map((m, i) => ({
    month: m,
    attendance: Math.min(99, Math.max(60, base + [0, -3, -9, 1, -2, -4, 0, -8, -16, -3, -5, -10][i])),
  }));
}

// ========== REGIONS (6 sample regions) ==========
const regionData: RegionSummary[] = [
  {
    regionId: 101, regionName: 'AGRA', stateCode: 'UP',
    metrics: { totalStudents: 12450, avgAttendance: 86.2, avgCompetency: 71.8, needsSupportCount: 1870, excellentCount: 2980, avgBehaviorScore: 74, avgPeerScore: 72, bmiNormalPercent: 68 },
    competencyBuckets: mkBuckets(12450, 24, 36, 26),
    topPerformers: [
      { id: 'R101-T1', name: 'KV No.1 Agra', metric: 'Avg Competency', value: 84 },
      { id: 'R101-T2', name: 'KV Mathura', metric: 'Attendance', value: 94 },
    ],
    alerts: [
      { id: 'R101-A1', name: 'KV Aligarh', issue: 'Attendance below 70%', severity: 'high' },
      { id: 'R101-A2', name: 'KV Firozabad', issue: '28% students need support', severity: 'medium' },
    ],
  },
  {
    regionId: 102, regionName: 'AHMEDABAD', stateCode: 'GJ',
    metrics: { totalStudents: 9870, avgAttendance: 89.1, avgCompetency: 76.3, needsSupportCount: 1085, excellentCount: 2860, avgBehaviorScore: 78, avgPeerScore: 76, bmiNormalPercent: 72 },
    competencyBuckets: mkBuckets(9870, 29, 38, 22),
    topPerformers: [
      { id: 'R102-T1', name: 'KV Gandhinagar', metric: 'Avg Competency', value: 88 },
      { id: 'R102-T2', name: 'KV Vadodara', metric: 'Behavior Score', value: 91 },
    ],
    alerts: [
      { id: 'R102-A1', name: 'KV Surat (Shift-II)', issue: '18% BMI abnormal', severity: 'low' },
    ],
  },
  {
    regionId: 103, regionName: 'BENGALURU', stateCode: 'KA',
    metrics: { totalStudents: 15230, avgAttendance: 91.4, avgCompetency: 79.1, needsSupportCount: 1370, excellentCount: 4420, avgBehaviorScore: 81, avgPeerScore: 80, bmiNormalPercent: 76 },
    competencyBuckets: mkBuckets(15230, 29, 37, 24),
    topPerformers: [
      { id: 'R103-T1', name: 'KV DRDO Bengaluru', metric: 'Avg Competency', value: 91 },
      { id: 'R103-T2', name: 'KV Mysuru', metric: 'Peer Score', value: 93 },
    ],
    alerts: [
      { id: 'R103-A1', name: 'KV Hubli', issue: 'Low parent engagement (42%)', severity: 'medium' },
    ],
  },
  {
    regionId: 104, regionName: 'CHANDIGARH', stateCode: 'CH',
    metrics: { totalStudents: 11600, avgAttendance: 88.3, avgCompetency: 74.5, needsSupportCount: 1508, excellentCount: 3130, avgBehaviorScore: 76, avgPeerScore: 74, bmiNormalPercent: 70 },
    competencyBuckets: mkBuckets(11600, 27, 35, 25),
    topPerformers: [
      { id: 'R104-T1', name: 'KV Sec-31 Chandigarh', metric: 'Avg Competency', value: 87 },
    ],
    alerts: [
      { id: 'R104-A1', name: 'KV Patiala', issue: 'Attendance drop 12% MoM', severity: 'high' },
    ],
  },
  {
    regionId: 105, regionName: 'DELHI', stateCode: 'DL',
    metrics: { totalStudents: 24800, avgAttendance: 87.6, avgCompetency: 75.9, needsSupportCount: 3224, excellentCount: 6944, avgBehaviorScore: 77, avgPeerScore: 75, bmiNormalPercent: 71 },
    competencyBuckets: mkBuckets(24800, 28, 36, 24),
    topPerformers: [
      { id: 'R105-T1', name: 'KV Andrews Ganj', metric: 'Avg Competency', value: 89 },
      { id: 'R105-T2', name: 'KV JNU Campus', metric: 'Attendance', value: 96 },
    ],
    alerts: [
      { id: 'R105-A1', name: 'KV Najafgarh', issue: '31% students below average competency', severity: 'high' },
      { id: 'R105-A2', name: 'KV Badarpur', issue: 'Low peer scores (avg 58)', severity: 'medium' },
    ],
  },
  {
    regionId: 106, regionName: 'KOLKATA', stateCode: 'WB',
    metrics: { totalStudents: 10340, avgAttendance: 84.8, avgCompetency: 70.2, needsSupportCount: 1654, excellentCount: 2480, avgBehaviorScore: 72, avgPeerScore: 70, bmiNormalPercent: 66 },
    competencyBuckets: mkBuckets(10340, 24, 33, 27),
    topPerformers: [
      { id: 'R106-T1', name: 'KV Fort William', metric: 'Peer Score', value: 88 },
    ],
    alerts: [
      { id: 'R106-A1', name: 'KV Kharagpur', issue: 'BMI abnormal 34%', severity: 'high' },
      { id: 'R106-A2', name: 'KV Midnapore', issue: 'Attendance below 72%', severity: 'medium' },
    ],
  },
];

// ========== NATIONAL OVERVIEW ==========
const natMetrics: LevelMetrics = {
  totalStudents: regionData.reduce((s, r) => s + r.metrics.totalStudents, 0),
  avgAttendance: +(regionData.reduce((s, r) => s + r.metrics.avgAttendance * r.metrics.totalStudents, 0) / regionData.reduce((s, r) => s + r.metrics.totalStudents, 0)).toFixed(1),
  avgCompetency: +(regionData.reduce((s, r) => s + r.metrics.avgCompetency * r.metrics.totalStudents, 0) / regionData.reduce((s, r) => s + r.metrics.totalStudents, 0)).toFixed(1),
  needsSupportCount: regionData.reduce((s, r) => s + r.metrics.needsSupportCount, 0),
  excellentCount: regionData.reduce((s, r) => s + r.metrics.excellentCount, 0),
  avgBehaviorScore: Math.round(regionData.reduce((s, r) => s + r.metrics.avgBehaviorScore * r.metrics.totalStudents, 0) / regionData.reduce((s, r) => s + r.metrics.totalStudents, 0)),
  avgPeerScore: Math.round(regionData.reduce((s, r) => s + r.metrics.avgPeerScore * r.metrics.totalStudents, 0) / regionData.reduce((s, r) => s + r.metrics.totalStudents, 0)),
  bmiNormalPercent: Math.round(regionData.reduce((s, r) => s + r.metrics.bmiNormalPercent * r.metrics.totalStudents, 0) / regionData.reduce((s, r) => s + r.metrics.totalStudents, 0)),
};

export const nationalOverview: NationalOverview = {
  metrics: natMetrics,
  competencyBuckets: mkBuckets(natMetrics.totalStudents, 27, 36, 25),
  attendanceTrend: mkTrend(88),
  regions: regionData,
  topPerformers: [
    { id: 'N-T1', name: 'Bengaluru Region', metric: 'Avg Competency', value: 79.1 },
    { id: 'N-T2', name: 'Ahmedabad Region', metric: 'Attendance', value: 89.1 },
    { id: 'N-T3', name: 'Delhi Region', metric: 'Excellent Students', value: 6944 },
  ],
  alerts: [
    { id: 'N-A1', name: 'Kolkata Region', issue: 'Lowest competency avg (70.2%)', severity: 'high' },
    { id: 'N-A2', name: 'Agra Region', issue: 'High support-need ratio (15%)', severity: 'medium' },
    { id: 'N-A3', name: 'Delhi Region', issue: '3,224 students flagged for support', severity: 'medium' },
  ],
};

// ========== SCHOOLS (for demo: schools under Delhi region) ==========
export const schoolsByRegion: Record<number, SchoolSummary[]> = {
  105: [
    {
      schoolId: 'KV-DL-001', schoolName: 'KV Andrews Ganj', location: 'South Delhi',
      metrics: { totalStudents: 1450, avgAttendance: 92.3, avgCompetency: 82.1, needsSupportCount: 116, excellentCount: 464, avgBehaviorScore: 82, avgPeerScore: 80, bmiNormalPercent: 78 },
      competencyBuckets: mkBuckets(1450, 32, 38, 20),
      attendanceTrend: mkTrend(92),
      topPerformers: [
        { id: 'S1-T1', name: 'Class 5A', metric: 'Competency', value: 89 },
        { id: 'S1-T2', name: 'Class 6B', metric: 'Attendance', value: 97 },
      ],
      alerts: [
        { id: 'S1-A1', name: 'Class 3B', issue: '22% below-avg competency', severity: 'medium' },
      ],
    },
    {
      schoolId: 'KV-DL-002', schoolName: 'KV JNU Campus', location: 'South Delhi',
      metrics: { totalStudents: 1280, avgAttendance: 94.1, avgCompetency: 84.5, needsSupportCount: 90, excellentCount: 448, avgBehaviorScore: 84, avgPeerScore: 83, bmiNormalPercent: 80 },
      competencyBuckets: mkBuckets(1280, 35, 37, 19),
      attendanceTrend: mkTrend(94),
      topPerformers: [
        { id: 'S2-T1', name: 'Class 6A', metric: 'Competency', value: 92 },
      ],
      alerts: [],
    },
    {
      schoolId: 'KV-DL-003', schoolName: 'KV Najafgarh', location: 'West Delhi',
      metrics: { totalStudents: 980, avgAttendance: 78.4, avgCompetency: 62.3, needsSupportCount: 304, excellentCount: 157, avgBehaviorScore: 64, avgPeerScore: 61, bmiNormalPercent: 60 },
      competencyBuckets: mkBuckets(980, 16, 28, 25),
      attendanceTrend: mkTrend(78),
      topPerformers: [
        { id: 'S3-T1', name: 'Class 4A', metric: 'Improvement', value: 12 },
      ],
      alerts: [
        { id: 'S3-A1', name: 'Overall', issue: '31% below-avg competency', severity: 'high' },
        { id: 'S3-A2', name: 'Class 5B', issue: 'Attendance 64%', severity: 'high' },
      ],
    },
    {
      schoolId: 'KV-DL-004', schoolName: 'KV Badarpur', location: 'South-East Delhi',
      metrics: { totalStudents: 1120, avgAttendance: 82.6, avgCompetency: 68.7, needsSupportCount: 213, excellentCount: 246, avgBehaviorScore: 70, avgPeerScore: 58, bmiNormalPercent: 64 },
      competencyBuckets: mkBuckets(1120, 22, 32, 26),
      attendanceTrend: mkTrend(82),
      topPerformers: [
        { id: 'S4-T1', name: 'Class 3A', metric: 'Peer Score', value: 82 },
      ],
      alerts: [
        { id: 'S4-A1', name: 'Overall', issue: 'Low peer scores (avg 58)', severity: 'medium' },
      ],
    },
    {
      schoolId: 'KV-DL-005', schoolName: 'KV Pragati Vihar', location: 'Central Delhi',
      metrics: { totalStudents: 1350, avgAttendance: 90.1, avgCompetency: 78.9, needsSupportCount: 162, excellentCount: 405, avgBehaviorScore: 80, avgPeerScore: 78, bmiNormalPercent: 74 },
      competencyBuckets: mkBuckets(1350, 30, 36, 22),
      attendanceTrend: mkTrend(90),
      topPerformers: [
        { id: 'S5-T1', name: 'Class 5A', metric: 'Competency', value: 87 },
      ],
      alerts: [],
    },
  ],
  // Fallback for other regions
  101: [
    {
      schoolId: 'KV-AG-001', schoolName: 'KV No.1 Agra', location: 'Agra',
      metrics: { totalStudents: 1200, avgAttendance: 88.5, avgCompetency: 76.4, needsSupportCount: 156, excellentCount: 336, avgBehaviorScore: 76, avgPeerScore: 74, bmiNormalPercent: 70 },
      competencyBuckets: mkBuckets(1200, 28, 36, 24),
      attendanceTrend: mkTrend(88),
      topPerformers: [{ id: 'SA1-T1', name: 'Class 6A', metric: 'Competency', value: 86 }],
      alerts: [{ id: 'SA1-A1', name: 'Class 4B', issue: 'Low attendance 71%', severity: 'medium' }],
    },
    {
      schoolId: 'KV-AG-002', schoolName: 'KV Mathura', location: 'Mathura',
      metrics: { totalStudents: 890, avgAttendance: 91.2, avgCompetency: 73.1, needsSupportCount: 125, excellentCount: 214, avgBehaviorScore: 74, avgPeerScore: 72, bmiNormalPercent: 68 },
      competencyBuckets: mkBuckets(890, 24, 35, 26),
      attendanceTrend: mkTrend(91),
      topPerformers: [{ id: 'SA2-T1', name: 'Class 5A', metric: 'Attendance', value: 96 }],
      alerts: [],
    },
  ],
};

// ========== CLASSES (for demo: classes under KV Andrews Ganj) ==========
export const classesBySchool: Record<string, ClassSummary[]> = {
  'KV-DL-001': [
    {
      classId: '3A', className: 'Class 3', section: 'A', teacherName: 'Mrs. Sunita Sharma',
      metrics: { totalStudents: 42, avgAttendance: 91.2, avgCompetency: 78.4, needsSupportCount: 5, excellentCount: 13, avgBehaviorScore: 80, avgPeerScore: 78, bmiNormalPercent: 76 },
      competencyBuckets: mkBuckets(42, 31, 36, 21),
      topPerformers: [{ id: 'C1-T1', name: 'Aarav Sharma', metric: 'Competency', value: 92 }],
      alerts: [{ id: 'C1-A1', name: 'Rohit K.', issue: 'Attendance 68%, needs follow-up', severity: 'high' }],
    },
    {
      classId: '3B', className: 'Class 3', section: 'B', teacherName: 'Mr. Rajesh Verma',
      metrics: { totalStudents: 40, avgAttendance: 85.8, avgCompetency: 68.2, needsSupportCount: 9, excellentCount: 6, avgBehaviorScore: 72, avgPeerScore: 70, bmiNormalPercent: 68 },
      competencyBuckets: mkBuckets(40, 15, 32, 30),
      topPerformers: [{ id: 'C2-T1', name: 'Priya P.', metric: 'Improvement +18%', value: 18 }],
      alerts: [
        { id: 'C2-A1', name: '9 students', issue: 'Below average competency', severity: 'high' },
        { id: 'C2-A2', name: 'Sita D.', issue: 'Underweight, low peer score', severity: 'high' },
      ],
    },
    {
      classId: '4A', className: 'Class 4', section: 'A', teacherName: 'Mrs. Kavita Gupta',
      metrics: { totalStudents: 45, avgAttendance: 93.4, avgCompetency: 81.7, needsSupportCount: 4, excellentCount: 16, avgBehaviorScore: 83, avgPeerScore: 81, bmiNormalPercent: 80 },
      competencyBuckets: mkBuckets(45, 36, 38, 18),
      topPerformers: [{ id: 'C3-T1', name: 'Vikram S.', metric: 'Most Improved', value: 14 }],
      alerts: [],
    },
    {
      classId: '4B', className: 'Class 4', section: 'B', teacherName: 'Mr. Anil Kumar',
      metrics: { totalStudents: 43, avgAttendance: 88.1, avgCompetency: 72.9, needsSupportCount: 7, excellentCount: 10, avgBehaviorScore: 74, avgPeerScore: 72, bmiNormalPercent: 72 },
      competencyBuckets: mkBuckets(43, 23, 35, 26),
      topPerformers: [{ id: 'C4-T1', name: 'Meera J.', metric: 'Behavior', value: 95 }],
      alerts: [{ id: 'C4-A1', name: '7 students', issue: 'Peer score < 50', severity: 'medium' }],
    },
    {
      classId: '5A', className: 'Class 5', section: 'A', teacherName: 'Mrs. Deepa Nair',
      metrics: { totalStudents: 44, avgAttendance: 94.8, avgCompetency: 85.3, needsSupportCount: 2, excellentCount: 20, avgBehaviorScore: 85, avgPeerScore: 84, bmiNormalPercent: 82 },
      competencyBuckets: mkBuckets(44, 45, 34, 14),
      topPerformers: [
        { id: 'C5-T1', name: 'Ananya R.', metric: 'Competency', value: 95 },
        { id: 'C5-T2', name: 'Karan M.', metric: 'Peer Score', value: 96 },
      ],
      alerts: [],
    },
    {
      classId: '5B', className: 'Class 5', section: 'B', teacherName: 'Mr. Suresh Pandey',
      metrics: { totalStudents: 41, avgAttendance: 86.5, avgCompetency: 70.1, needsSupportCount: 8, excellentCount: 7, avgBehaviorScore: 71, avgPeerScore: 68, bmiNormalPercent: 66 },
      competencyBuckets: mkBuckets(41, 17, 32, 27),
      topPerformers: [{ id: 'C6-T1', name: 'Amit V.', metric: 'Attendance 98%', value: 98 }],
      alerts: [
        { id: 'C6-A1', name: 'Sita Devi', issue: 'Underweight, attendance 62%, needs counselor', severity: 'high' },
        { id: 'C6-A2', name: '8 students', issue: 'Below average competency', severity: 'medium' },
      ],
    },
    {
      classId: '6A', className: 'Class 6', section: 'A', teacherName: 'Mrs. Priya Kapoor',
      metrics: { totalStudents: 46, avgAttendance: 93.7, avgCompetency: 83.6, needsSupportCount: 3, excellentCount: 18, avgBehaviorScore: 82, avgPeerScore: 80, bmiNormalPercent: 78 },
      competencyBuckets: mkBuckets(46, 39, 35, 17),
      topPerformers: [{ id: 'C7-T1', name: 'Meera Joshi', metric: 'All-round', value: 95 }],
      alerts: [],
    },
    {
      classId: '6B', className: 'Class 6', section: 'B', teacherName: 'Mr. Vikash Mishra',
      metrics: { totalStudents: 44, avgAttendance: 95.2, avgCompetency: 80.8, needsSupportCount: 4, excellentCount: 15, avgBehaviorScore: 79, avgPeerScore: 77, bmiNormalPercent: 76 },
      competencyBuckets: mkBuckets(44, 34, 36, 20),
      topPerformers: [{ id: 'C8-T1', name: 'Sneha K.', metric: 'Attendance 99%', value: 99 }],
      alerts: [{ id: 'C8-A1', name: 'Ravi M.', issue: 'Behavior score dropped 15 pts', severity: 'low' }],
    },
  ],
};

// ========== Lookup helpers ==========
export function getRegionById(regionId: number): RegionSummary | undefined {
  return nationalOverview.regions.find(r => r.regionId === regionId);
}

export function getSchoolsForRegion(regionId: number): SchoolSummary[] {
  return schoolsByRegion[regionId] || schoolsByRegion[105] || [];
}

export function getClassesForSchool(schoolId: string): ClassSummary[] {
  return classesBySchool[schoolId] || classesBySchool['KV-DL-001'] || [];
}

export function getSchoolById(regionId: number, schoolId: string): SchoolSummary | undefined {
  return getSchoolsForRegion(regionId).find(s => s.schoolId === schoolId);
}

export function getClassById(schoolId: string, classId: string): ClassSummary | undefined {
  return getClassesForSchool(schoolId).find(c => c.classId === classId);
}
